import { Container, Graphics, LINE_CAP, LINE_JOIN, Text } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { chamfer, RIM, RIM_LIGHT, TIMBER, TIMBER_EDGE } from "./build_board"
import { drawWheelIcon, WHEEL_ICON_BOX, wheelHubIconFor, wheelIconFor } from "./command_wheel_icons"

// The command wheel: a forged ring of orders the player picks from by direction.
//
// What it is drawn as, and why. The wheel is a DECISION -- every time it is up, the player is being
// asked to choose -- so it has to read in one glance and feel good to answer. The previous wheel
// printed each order's name inside its wedge, which is the wrong shape for words: on the sides of the
// wheel a wedge is tall where a word is wide, so six orders meant two-line labels shrunk to fit and
// running into each other at the seams. It was also a see-through disc, so the world and the HUD
// text under it bled through the very labels being read.
//
//   the RING      carries a glyph per order (command_wheel_icons.ts). A glyph fits a wedge at any
//                 angle, so the ring can be smaller than the old disc and still roomy.
//   the PLAQUES   carry the names, one line each, on timber tags just past the rim where there is
//                 room for a whole word. Each plaque is a target too.
//   the HUB       is a bronze medallion with the subject's glyph -- the role being ordered, or the
//                 person/hand of an interaction page.
//
// It is built in the HUD's own materials -- the forged bezel and rivets of the wave dial (dial.ts),
// the cut timber and bronze of the build board (build_board.ts) -- so it belongs to the camp rather
// than floating over it. The face is OPAQUE: nothing behind it competes with the choice.
//
// The feel is in the motion, all of it driven off the frame delta handed to `animate` (the widget owns
// no timer): the ring pops open and the orders fan out round it one after another; the wedge under
// the pointer fills with firelight, its glyph lifts and gilds, and a brass needle on the hub swings
// to it; a pick flashes the wedge, throws a ripple off the rim and lifts the wheel away. No filters
// anywhere -- every glow is a stack of faint fills -- for the low-end hardware this game targets.

// Geometry, as fractions of the ring's outer radius, so a bigger wheel is the same wheel.
const BEZEL_RATIO = 0.095 // the forged band, painted inside the radius
const HUB_RATIO = 0.3 // the medallion in the middle
const ICON_RATIO = 0.31 // a slice glyph's drawn size
// How far past the rim a press still counts as the slice it points into. The wheel picks by
// DIRECTION, so overshooting the rim a little is aiming, not missing.
const RING_SLOP = 12
const SINGLE_COMMAND_RADIUS = 22

// The name plaques.
const PLAQUE_GAP = 5 // clear air between the rim and the nearest point of a plaque
const PLAQUE_PAD_X = 7
const PLAQUE_H = 17
const PLAQUE_NOTE_H = 10 // extra height when a plaque carries a note ("too far")
const PLAQUE_MIN_W = 34
const PLAQUE_SEPARATION = 3 // closest two plaques may come to each other
const PLAQUE_CHAMFER = 4
const PLAQUE_HIT_PAD = 3
const LABEL_FONT_SIZE = 10
const NOTE_FONT_SIZE = 8
// The global wheel's title ("ALL FORAGERS"), which also switches the role it broadcasts to.
const BANNER_FONT_SIZE = 9
const BANNER_H = 18
const BANNER_PAD_X = 22 // room either side of the title for its switch chevrons
const BANNER_GAP = 6

// Palette. The bezel, rim and rivets are build_board's; the light is the countdown dial's fire.
const FACE_COLOR = 0x16110c
const FACE_WARM = 0x2b1e13 // a faint warmth pooled at the centre so the face is not a flat plate
const ICON_INK = 0xead7b2 // resting glyph: parchment
const LABEL_INK = 0xffedc7 // build_board INK
const GOLD = 0xffd166 // build_board GOLD -- what a chosen thing is lit with
const EMBER = 0xff9d2e // dial COUNTDOWN_FLARE -- the firelight pooled in a hovered wedge
const MUTED = 0xa3916f // an order that is offered but cannot be carried out from here
const NOTE_COLOR = 0xf09a6c
const LABEL_RECEDED = 0xa89a80 // a name while another order is under the pointer
const PLAQUE_FACE = 0x1a140f
const PLAQUE_FACE_LIT = 0x33240f
const HUB_FACE = 0x110c08

// Motion, in ms.
const OPEN_MS = 170
const REVEAL_STAGGER_MS = 24 // each order fans out this long after the one before it
const REVEAL_ITEM_MS = 160
const HOVER_MS = 90 // a wedge lighting up (or going dark)
const FLASH_MS = 420 // the confirmation flash on a pick
const CLOSE_MS = 140
const COMMIT_HOLD_MS = 110 // a pick holds the lit wheel this long before it goes, so the flash registers
const NEEDLE_MS = 45 // time constant of the hub needle swinging round
const HUB_HOVER_MS = 110

export interface CommandWheelProps {
    // Outer radius of the ring (the forged bezel's edge). The name plaques sit outside it -- see
    // getExtent for the wheel's whole footprint.
    radius?: number
    onCommandSelect?: (command: string, npcType: string) => void
    // The pointer has moved onto a different order. For a soft tick, so aiming has a feel to it.
    onHoverChange?: () => void
}

export type CommandWheelRole = "forager" | "defender" | "lord"

// The synthetic page name the wheel wears when it is listing an entity's interactions rather than an
// NPC role's commands. gui_manager routes a selection on this page to ACTIVATE_ENTITY_INTERACTION
// instead of the squad command channel.
export const INTERACTION_PAGE = "interactions"

// The one command that asks for a place as well as an order: selecting it hands the pointer to the
// location picker, and the order only goes out once the player has clicked a spot (see GuiManager's
// onCommandSelect and PlayerController.beginLocationPick). Named here so the wheel's command table
// and the code that special-cases it can't drift apart.
export const HOLD_POSITION_COMMAND = "defender:hold"

// "Change Role" on either role page. Not an order to the NPC: it asks for the role picker, which the
// server raises -- so the client has to recognise it on the way out to remember WHERE the wheel that
// sent it was, and put the picker in the same place (see GuiManager.onCommandSelect).
export const isChangeRoleCommand = (command: string) => command.endsWith(":change_role")

// Per-role accent for the per-NPC (role-locked) wheel — matches ROLE_COLORS in ember_roster.ts,
// hand-synced to avoid an import cycle (command_wheel ← gui_manager → ember_roster). The global
// (broadcast) wheel keeps the neutral amber WHEEL_ACCENT instead.
const ROLE_ACCENT: Record<CommandWheelRole, number> = {
    forager: 0x7be08a,
    defender: 0xff8a5c,
    lord: 0xf2a94e,
}
const WHEEL_ACCENT = 0xf2a94e

export interface NPCCommand {
    name: string
    displayName: string
    // Only listed when the wheel is bound to ONE NPC. The global (Control-key) wheel broadcasts to a
    // whole role, and some orders have no sensible mass form -- see the table below.
    perEntityOnly?: boolean
}

export interface NPCType {
    name: string
    displayName: string
    emoji?: string
    commands: NPCCommand[]
}

// ---- pure geometry (exported for the tests) -------------------------------------------------

// Slice `index` of `count` is centred on this angle. Slice 0 starts at twelve o'clock and they run
// clockwise, so a two-order wheel splits left/right and a four-order wheel is an X -- both keep the
// plaques off the top and bottom, where a screen 460 units tall has the least room to give.
export const sliceCenterAngle = (index: number, count: number): number =>
    (index + 0.5) * ((Math.PI * 2) / count) - Math.PI / 2

// Which slice a direction points into (the inverse of the above).
export const sliceIndexAt = (x: number, y: number, count: number): number => {
    if (count <= 0) return -1
    const angle = Math.atan2(y, x) + Math.PI / 2
    const normalized = angle < 0 ? angle + Math.PI * 2 : angle
    return Math.min(count - 1, Math.floor(normalized / ((Math.PI * 2) / count)))
}

export interface PlaqueBox {
    // Centre, relative to the wheel's centre, and size.
    x: number
    y: number
    w: number
    h: number
}

// Nearest any point of a centred box comes to the origin.
const boxReach = (cx: number, cy: number, w: number, h: number) =>
    Math.hypot(Math.max(0, Math.abs(cx) - w / 2), Math.max(0, Math.abs(cy) - h / 2))

const boxesOverlap = (a: PlaqueBox, b: PlaqueBox, separation: number) =>
    Math.abs(a.x - b.x) < (a.w + b.w) / 2 + separation && Math.abs(a.y - b.y) < (a.h + b.h) / 2 + separation

// Put a plaque on the ray along `angle`, as close in as it can sit while every point of it stays
// `clearance` from the centre. A plaque is a BOX: on a diagonal its inner corner reaches in further
// than its centre does, so the centre's distance alone would let a wide label bite into the rim.
export const placePlaque = (angle: number, w: number, h: number, clearance: number): { x: number; y: number; t: number } => {
    const ux = Math.cos(angle)
    const uy = Math.sin(angle)
    let lo = 0
    let hi = clearance + w + h
    for (let step = 0; step < 32; step++) {
        const mid = (lo + hi) / 2
        if (boxReach(mid * ux, mid * uy, w, h) >= clearance) hi = mid
        else lo = mid
    }
    return { x: hi * ux, y: hi * uy, t: hi }
}

// Lay out every plaque round a ring: each on its own slice's ray, then any two that touch walked
// outward along their rays together until they clear. Rays diverge, so this always ends.
export const layoutPlaques = (
    sizes: Array<{ w: number; h: number }>,
    count: number,
    clearance: number,
): PlaqueBox[] => {
    const placed = sizes.map((size, i) => {
        const angle = sliceCenterAngle(i, count)
        return { ...size, ...placePlaque(angle, size.w, size.h, clearance), angle }
    })
    for (let pass = 0; pass < 200; pass++) {
        let moved = false
        for (let i = 0; i < placed.length; i++) {
            for (let j = i + 1; j < placed.length; j++) {
                if (!boxesOverlap(placed[i], placed[j], PLAQUE_SEPARATION)) continue
                for (const p of [placed[i], placed[j]]) {
                    p.t += 1
                    p.x = Math.cos(p.angle) * p.t
                    p.y = Math.sin(p.angle) * p.t
                }
                moved = true
            }
        }
        if (!moved) break
    }
    return placed.map(({ x, y, w, h }) => ({ x: Math.round(x), y: Math.round(y), w, h }))
}

// "Give Food (too far)" is an order AND a reason it cannot be carried out from here. The plaque
// prints the order and puts the parenthetical under it, smaller, instead of wrapping it into four
// lines; the slice dims to say the same thing.
export const splitWheelLabel = (displayName: string): { label: string; note?: string } => {
    const match = /^(.*\S)\s*\(([^)]+)\)$/.exec(displayName)
    return match ? { label: match[1], note: match[2] } : { label: displayName }
}

// ---- small helpers ----------------------------------------------------------------------------

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
const easeInQuad = (t: number) => t * t
const easeOutBack = (t: number) => {
    const c1 = 1.7
    const c3 = c1 + 1
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)
}
const lerpColor = (from: number, to: number, t: number) => {
    const k = clamp01(t)
    const r = ((from >> 16) & 0xff) + ((((to >> 16) & 0xff) - ((from >> 16) & 0xff)) * k)
    const g = ((from >> 8) & 0xff) + ((((to >> 8) & 0xff) - ((from >> 8) & 0xff)) * k)
    const b = (from & 0xff) + (((to & 0xff) - (from & 0xff)) * k)
    return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b)
}
const approach = (value: number, target: number, step: number) =>
    value < target ? Math.min(target, value + step) : Math.max(target, value - step)
const wrapAngle = (a: number) => Math.atan2(Math.sin(a), Math.cos(a))

// An annulus segment as one path, so a fill covers exactly one wedge of the ring.
const annularSector = (g: Graphics, inner: number, outer: number, from: number, to: number) => {
    g.moveTo(Math.cos(from) * inner, Math.sin(from) * inner)
    g.lineTo(Math.cos(from) * outer, Math.sin(from) * outer)
    g.arc(0, 0, outer, from, to)
    g.lineTo(Math.cos(to) * inner, Math.sin(to) * inner)
    g.arc(0, 0, inner, to, from, true)
    g.closePath()
}

const makeText = (text: string, fontSize: number, letterSpacing = 0): Text => {
    const node = new TextRenderer().fontSize(fontSize).bold().strokeThickness(0).align("center").render(text) as Text
    if (letterSpacing) {
        node.style.letterSpacing = letterSpacing
    }
    return node
}

interface SliceView {
    command: NPCCommand
    angle: number
    icon: Graphics
    iconShadow: Graphics
    plaque: Container
    plaqueBg: Graphics
    label: Text
    note?: Text
    box: PlaqueBox
    muted: boolean
    // 0..1, eased toward 1 while this slice is under the pointer.
    glow: number
}

interface BannerView {
    container: Container
    bg: Graphics
    title: Text
    box: PlaqueBox
}

type Phase = "closed" | "opening" | "open" | "closing"

type PointerTarget = { kind: "slice"; index: number } | { kind: "hub" } | { kind: "banner"; step: number } | null

export class CommandWheel extends Graphics {
    private normalRadius: number
    private compactRequested = false
    private compact = false
    private get radius() { return this.compact ? SINGLE_COMMAND_RADIUS : this.normalRadius }
    private onCommandSelect?: (command: string, npcType: string) => void
    private onHoverChange?: () => void

    // Everything drawn lives in `content`, which the open/close motion scales. The wheel itself only
    // fades, so its position, hit area and pointer maths stay in unscaled coordinates.
    private content = new Container()
    private frame = new Graphics() // shadow, bezel, face, spokes, rivets: redrawn per layout only
    private light = new Graphics() // firelight in the hovered wedge + the pick flash: per animated frame
    private iconLayer = new Container()
    private hub = new Graphics()
    private hubGlyph?: Container
    private overlay = new Graphics() // needle, hub glow, pick ripple
    private plaqueLayer = new Container()
    private slices: SliceView[] = []
    private banner?: BannerView

    private currentNPCIndex: number = 0
    // The ROLE page last shown, which the global wheel goes back to once an entity's interaction page
    // is dropped. (Clamping the index instead put the Control-key wheel on Defenders after every
    // chest or visitor, whatever page the player had been using.)
    private rolePageIndex: number = 0
    private activeCommandIndex: number = -1
    private hoveredCommandIndex: number = -1
    private hubHovered = false
    // When set, the wheel commands one specific NPC (opened from that entity's command button)
    // rather than broadcasting to its whole role. Cleared for the global Control-key wheel.
    private targetEntityId?: string
    // While targeting a single NPC the role page is locked (center switcher disabled).
    private roleLocked: boolean = false
    private commandOverride?: NPCCommand[]

    // Open/close state. `opened` is the logical flag (flips the instant a close starts) so callers
    // see the wheel as "closed" the moment it begins dismissing, while `phase` keeps it drawing until
    // its exit has played.
    private opened: boolean = false
    private phase: Phase = "closed"
    private phaseMs = 0
    private commitClose = false
    // A page change asked for while the wheel is on its way out waits until it has gone, so the exit
    // shows what the player was looking at rather than the global wheel it is being reset to.
    private layoutPending = false
    // ms since the orders started fanning out; undefined once they all have.
    private revealMs?: number
    // The pick flash: which slice, and how far through.
    private flashIndex = -1
    private flashMs = 0
    private needleAngle = -Math.PI / 2
    private needleAlpha = 0
    private hubGlow = 0

    // "Change Role" is not an order to the NPC at all: it puts the role picker up over it and waits,
    // and it is `perEntityOnly` so the GLOBAL (Control-key) wheel never lists it. Re-crewing the whole
    // camp in one click is not a thing anybody means to do, and the question the picker asks ("which
    // job instead?") has no mass form -- it would stack one panel per member on the asking player's
    // screen. The server refuses an untargeted change_role as well (see handleCommand in
    // ember-forager.js / ember-defender.js); this is what keeps it off the wheel in the first place,
    // so the player is never offered an order that silently does nothing.
    //
    // There is deliberately NO "Rename" here. Naming a member is offered once, at the hire, as the
    // last beat of the role picker (ember-recruit.js chooseRole -> the name prompt), and that is the
    // whole of it: a standing order to re-open a text field is a settings screen, not a camp order,
    // and it crowded out the orders that actually move somebody. The scripts still answer a
    // "<role>:rename" command (and the name prompt, and RENAME_ENTITY, are untouched), so putting the
    // verb back is a line in the tables below.
    private npcTypes: NPCType[] = [
        {
            name: "forager",
            displayName: "Forager",
            commands: [
                { name: "forager:forage", displayName: "Forage Wood" },
                { name: "forager:feed_fire", displayName: "Feed Fire" },
                { name: "forager:head_back", displayName: "Head Back" },
                { name: "forager:repair", displayName: "Repair Walls" },
                { name: "forager:follow", displayName: "Follow Me" },
                { name: "forager:change_role", displayName: "Change Role", perEntityOnly: true },
            ],
        },
        {
            name: "defender",
            displayName: "Defender",
            commands: [
                { name: "defender:defend", displayName: "Defend Camp" },
                { name: HOLD_POSITION_COMMAND, displayName: "Hold Position" },
                { name: "defender:follow", displayName: "Follow Me" },
                { name: "defender:change_role", displayName: "Change Role", perEntityOnly: true },
                { name: "defender:watch", displayName: "Watch", perEntityOnly: true },
            ],
        },
    ]

    // THE command list for a page -- every layout, hit test and selection reads the wheel through
    // this, so a hidden command cannot be laid out, hovered, clicked or reported. `perEntityOnly`
    // commands are dropped unless the wheel is bound to a single NPC (roleLocked), which covers the
    // per-entity wheel, the roster wheel and the interactions page, and excludes only the global
    // (Control-key) broadcast wheel.
    private commandsOf(npcType: NPCType): NPCCommand[] {
        if (this.roleLocked) {
            return this.commandOverride || npcType.commands
        }
        return npcType.commands.filter(command => !command.perEntityOnly)
    }

    constructor(props: CommandWheelProps = {}) {
        super()

        this.normalRadius = props.radius || 64
        this.onCommandSelect = props.onCommandSelect
        this.onHoverChange = props.onHoverChange

        this.content.addChild(this.frame, this.light, this.iconLayer, this.hub, this.overlay, this.plaqueLayer)
        this.addChild(this.content)

        this.interactive = true
        this.hitArea = { contains: (x: number, y: number) => this.coversPoint(x, y) }

        this.on("pointermove", this.onPointerMove.bind(this))
        this.on("pointerdown", this.onPointerDown.bind(this))
        this.on("pointerout", this.onPointerOut.bind(this))

        // Prevent context menu from appearing
        this.on("rightclick", event => {
            event.stopPropagation()
        })
        this.on("contextmenu", event => {
            event.preventDefault()
            event.stopPropagation()
        })

        this.layout()
    }

    // ---- geometry ----

    private get bezelWidth() {
        return Math.max(4, this.radius * BEZEL_RATIO)
    }

    private get faceRadius() {
        return this.radius - this.bezelWidth
    }

    private get hubRadius() {
        return this.radius * HUB_RATIO
    }

    // Glyphs ride the middle of the band between the hub and the face's edge.
    private get iconRadius() {
        if (this.compact) return 0
        return (this.hubRadius + this.faceRadius) / 2 + 1
    }

    private get iconScale() {
        if (this.compact) return 22 / WHEEL_ICON_BOX
        return (this.radius * ICON_RATIO) / WHEEL_ICON_BOX
    }

    private get switchable() {
        return !this.roleLocked && this.npcTypes.length > 1
    }

    // The ring's outer radius, so callers can anchor chrome of their own (e.g. the tether line
    // gui_manager draws to the commanded NPC) on the rim rather than the center.
    public getRadius(): number {
        return this.radius
    }

    // How far the wheel paints from its centre in each direction -- ring, shadow, plaques and title
    // -- for laying it out against the screen edge. The plaques make it wider than it is tall, and
    // how much depends on the names on show, so this is read after a bind, never assumed.
    public getExtent(): { left: number; right: number; top: number; bottom: number } {
        const extent = { left: this.radius, right: this.radius, top: this.radius, bottom: this.radius + 3 }
        const boxes = this.slices.map(slice => slice.box)
        if (this.banner) boxes.push(this.banner.box)
        for (const box of boxes) {
            extent.left = Math.max(extent.left, box.w / 2 - box.x)
            extent.right = Math.max(extent.right, box.x + box.w / 2)
            extent.top = Math.max(extent.top, box.h / 2 - box.y)
            extent.bottom = Math.max(extent.bottom, box.y + box.h / 2 + 2)
        }
        return extent
    }

    // Identity color for the wheel's chrome: a per-NPC (role-locked) wheel takes its role's accent
    // so it reads as "this one unit"; the global broadcast wheel stays neutral amber. Public so the
    // tether line to the commanded NPC can wear the same accent as the wheel it leaves.
    public getAccent(): number {
        if (this.roleLocked) {
            const role = this.npcTypes[this.currentNPCIndex].name as CommandWheelRole
            return ROLE_ACCENT[role] ?? WHEEL_ACCENT
        }
        return WHEEL_ACCENT
    }

    // The global wheel's hub glyph and title wear the ROLE's colour (it is the role being ordered);
    // the wheel's own accent stays amber, which is what tells it apart from a one-member wheel.
    private glyphColor(): number {
        const page = this.npcTypes[this.currentNPCIndex]
        return ROLE_ACCENT[page.name as CommandWheelRole] ?? this.getAccent()
    }

    // ---- building a page ----

    // Rebuild everything that depends on the page: the frame (spoke count), the hub, a glyph and a
    // plaque per order, and -- on the global wheel -- the title. Not called on hover: hovering only
    // repaints the light and moves what is already here.
    private layout() {
        this.compact = this.compactRequested
        this.layoutPending = false
        for (const slice of this.slices) {
            slice.icon.destroy()
            slice.iconShadow.destroy()
            slice.plaque.destroy({ children: true })
        }
        this.slices = []
        if (this.banner) {
            this.banner.container.destroy({ children: true })
            this.banner = undefined
        }

        const page = this.npcTypes[this.currentNPCIndex]
        const commands = this.commandsOf(page)
        const count = commands.length

        const views = commands.map((command, index) => {
            const angle = sliceCenterAngle(index, count)
            const { label, note } = splitWheelLabel(command.displayName)
            const iconKey = wheelIconFor(command.name, label)

            const icon = new Graphics()
            drawWheelIcon(icon, iconKey)
            // Same geometry, painted black and dropped a pixel: the glyph reads as struck INTO the
            // face rather than printed on it, and it costs no second tessellation.
            const iconShadow = new Graphics(icon.geometry)
            iconShadow.tint = 0x000000
            const cx = Math.cos(angle) * this.iconRadius
            const cy = Math.sin(angle) * this.iconRadius
            icon.position.set(cx, cy)
            iconShadow.position.set(cx, cy + 1.2)
            this.iconLayer.addChild(iconShadow, icon)

            const plaque = new Container()
            const plaqueBg = new Graphics()
            const labelText = makeText(label, LABEL_FONT_SIZE)
            plaque.addChild(plaqueBg, labelText)
            let noteText: Text | undefined
            if (note) {
                noteText = makeText(note, NOTE_FONT_SIZE)
                noteText.tint = NOTE_COLOR
                plaque.addChild(noteText)
            }
            this.plaqueLayer.addChild(plaque)
            const w = Math.ceil(Math.max(PLAQUE_MIN_W, labelText.width + PLAQUE_PAD_X * 2, (noteText?.width ?? 0) + PLAQUE_PAD_X * 2))
            const h = PLAQUE_H + (noteText ? PLAQUE_NOTE_H : 0)
            return { command, angle, icon, iconShadow, plaque, plaqueBg, label: labelText, note: noteText, w, h, muted: !!note }
        })

        const boxes = this.compact
            ? views.map(({ w, h }) => ({ x: 0, y: this.radius + PLAQUE_GAP + h / 2, w, h }))
            : layoutPlaques(views.map(({ w, h }) => ({ w, h })), count, this.radius + PLAQUE_GAP)
        this.slices = views.map((view, index) => {
            const box = boxes[index]
            const top = -box.h / 2
            view.label.position.set(Math.round(-view.label.width / 2), Math.round(top + (PLAQUE_H - view.label.height) / 2))
            if (view.note) {
                view.note.position.set(Math.round(-view.note.width / 2), Math.round(top + PLAQUE_H - 3))
            }
            view.plaque.position.set(box.x, box.y)
            return {
                command: view.command,
                angle: view.angle,
                icon: view.icon,
                iconShadow: view.iconShadow,
                plaque: view.plaque,
                plaqueBg: view.plaqueBg,
                label: view.label,
                note: view.note,
                box,
                muted: view.muted,
                glow: 0,
            }
        })

        if (this.switchable) {
            this.buildBanner(page)
        }

        this.drawFrame(count)
        this.drawHub(page)
        this.hub.visible = !this.compact
        if (this.hubGlyph) this.hubGlyph.visible = !this.compact
        this.paint()
    }

    // The global wheel names who it is ordering -- the whole role -- and doubles as the role switch:
    // its chevrons say "there is another page", and a press on either side turns to it.
    private buildBanner(page: NPCType) {
        const container = new Container()
        const bg = new Graphics()
        const title = makeText(`All ${page.displayName}s`.toUpperCase(), BANNER_FONT_SIZE, 1)
        title.tint = this.glyphColor()
        container.addChild(bg, title)
        // pixi's letterSpacing adds a trailing advance after the last glyph; take it back off so the
        // title sits on the wheel's axis.
        const inkWidth = title.width - 1
        const w = Math.ceil(inkWidth + BANNER_PAD_X * 2)
        title.position.set(Math.round(-inkWidth / 2), Math.round(-title.height / 2))
        // Above the ring, and above any plaque it would otherwise land on.
        let y = -(this.radius + BANNER_GAP + BANNER_H / 2)
        for (let guard = 0; guard < 20; guard++) {
            const box = { x: 0, y, w, h: BANNER_H }
            const blocker = this.slices.find(slice => boxesOverlap(box, slice.box, PLAQUE_SEPARATION))
            if (!blocker) break
            y = blocker.box.y - blocker.box.h / 2 - PLAQUE_SEPARATION - BANNER_H / 2 - 1
        }
        container.position.set(0, Math.round(y))
        this.plaqueLayer.addChild(container)
        this.banner = { container, bg, title, box: { x: 0, y: Math.round(y), w, h: BANNER_H } }
    }

    // The static ring: dropped shadow, forged bezel lit from the upper left, an opaque face with a
    // little warmth at its heart, an engraved spoke between each pair of orders and a rivet where each
    // spoke meets the bezel.
    private drawFrame(count: number) {
        const g = this.frame
        const r = this.radius
        const face = this.faceRadius
        const band = r - this.bezelWidth / 2
        g.clear()

        g.beginFill(0x000000, 0.36).drawCircle(0, 3, r + 0.5).endFill()
        g.beginFill(TIMBER, 1).drawCircle(0, 0, r).endFill()
        const sweep = (from: number, to: number, color: number, alpha: number) => {
            g.lineStyle({ width: this.bezelWidth * 0.6, color, alpha, cap: LINE_CAP.ROUND })
            g.moveTo(Math.cos(from) * band, Math.sin(from) * band)
            g.arc(0, 0, band, from, to)
        }
        sweep(Math.PI * 1.04, Math.PI * 1.88, RIM_LIGHT, 0.45)
        sweep(Math.PI * 0.08, Math.PI * 0.86, 0x000000, 0.4)
        g.lineStyle({ width: 1.2, color: TIMBER_EDGE, alpha: 0.95 }).drawCircle(0, 0, r)
        g.lineStyle(0)

        g.beginFill(FACE_COLOR, 0.97).drawCircle(0, 0, face).endFill()
        // Many faint steps rather than a few strong ones: a few strong discs read as a target.
        for (let ring = 0; ring < 7; ring++) {
            g.beginFill(FACE_WARM, 0.09).drawCircle(0, 0, face * (0.92 - ring * 0.09)).endFill()
        }
        g.lineStyle({ width: 1, color: 0x000000, alpha: 0.6 }).drawCircle(0, 0, face - 0.5)

        const spokeAngles = count > 1 ? Array.from({ length: count }, (_, i) => (i * Math.PI * 2) / count - Math.PI / 2) : []
        const inner = this.hubRadius + 1
        for (const angle of spokeAngles) {
            const ux = Math.cos(angle)
            const uy = Math.sin(angle)
            // A groove: a dark cut with a hairline of light along its lower edge.
            g.lineStyle({ width: 2, color: 0x000000, alpha: 0.6, cap: LINE_CAP.ROUND })
            g.moveTo(ux * inner, uy * inner).lineTo(ux * (face - 1), uy * (face - 1))
            g.lineStyle({ width: 1, color: RIM_LIGHT, alpha: 0.12, cap: LINE_CAP.ROUND })
            g.moveTo(ux * inner - uy * 1.2, uy * inner + ux * 1.2).lineTo(ux * (face - 1) - uy * 1.2, uy * (face - 1) + ux * 1.2)
        }
        g.lineStyle(0)
        // Rivets where the spokes meet the bezel, or on the diagonals when there are no spokes.
        const rivets = spokeAngles.length
            ? spokeAngles
            : [0, 1, 2, 3].map(k => -Math.PI / 4 + (k * Math.PI) / 2)
        for (const angle of rivets) {
            const x = Math.cos(angle) * band
            const y = Math.sin(angle) * band
            g.beginFill(0x000000, 0.5).drawCircle(x, y + 0.5, 2.2).endFill()
            g.beginFill(RIM, 0.95).drawCircle(x, y, 1.75).endFill()
            g.beginFill(RIM_LIGHT, 0.7).drawCircle(x - 0.5, y - 0.6, 0.7).endFill()
        }
    }

    // The medallion: a raised bronze boss with a dark face, ringed in the subject's colour, carrying
    // its glyph.
    private drawHub(page: NPCType) {
        const g = this.hub
        const r = this.hubRadius
        const color = this.glyphColor()
        g.clear()
        g.beginFill(0x000000, 0.5).drawCircle(0, 1.6, r + 0.8).endFill()
        g.beginFill(RIM, 1).drawCircle(0, 0, r).endFill()
        g.lineStyle({ width: 1.6, color: RIM_LIGHT, alpha: 0.6, cap: LINE_CAP.ROUND })
        g.moveTo(Math.cos(Math.PI * 1.05) * (r - 1.1), Math.sin(Math.PI * 1.05) * (r - 1.1))
        g.arc(0, 0, r - 1.1, Math.PI * 1.05, Math.PI * 1.85)
        g.lineStyle({ width: 1, color: TIMBER_EDGE, alpha: 0.9 }).drawCircle(0, 0, r)
        g.lineStyle(0)
        g.beginFill(HUB_FACE, 1).drawCircle(0, 0, r - 2.6).endFill()
        g.lineStyle({ width: 1, color, alpha: 0.45 }).drawCircle(0, 0, r - 4.4)
        g.lineStyle(0)

        if (this.hubGlyph) {
            this.hubGlyph.destroy()
            this.hubGlyph = undefined
        }
        const iconKey = wheelHubIconFor(page.name, page.emoji)
        if (iconKey) {
            const glyph = new Graphics()
            drawWheelIcon(glyph, iconKey)
            glyph.scale.set((r * 1.02) / WHEEL_ICON_BOX)
            glyph.tint = color
            this.hubGlyph = glyph
        } else {
            const glyph = new TextRenderer().fontSize(Math.round(r)).align("center").render(page.emoji)
            glyph.position.set(-glyph.width / 2, -glyph.height / 2)
            this.hubGlyph = glyph
        }
        this.content.addChildAt(this.hubGlyph, this.content.getChildIndex(this.hub) + 1)
    }

    // ---- painting (anything that moves) ----

    private revealProgress(index: number) {
        if (this.revealMs === undefined) return 1
        return clamp01((this.revealMs - index * REVEAL_STAGGER_MS) / REVEAL_ITEM_MS)
    }

    private get revealTotalMs() {
        return Math.max(0, this.slices.length - 1) * REVEAL_STAGGER_MS + REVEAL_ITEM_MS
    }

    private sliceSpan(index: number): [number, number] {
        const count = this.slices.length
        if (count <= 1) return [-Math.PI / 2, Math.PI * 1.5]
        const width = (Math.PI * 2) / count
        const from = index * width - Math.PI / 2
        // Stop the light a hair short of the spokes, so the grooves stay cut.
        return [from + 0.025, from + width - 0.025]
    }

    private paint() {
        // The whole wheel: a pop on the way in, a sink (dismissed) or a lift (picked) on the way out.
        let alpha = 1
        let scale = 1
        if (this.phase === "opening") {
            const t = clamp01(this.phaseMs / OPEN_MS)
            alpha = easeOutCubic(clamp01(t * 1.5))
            scale = 0.84 + 0.16 * easeOutBack(t)
        } else if (this.phase === "closing") {
            if (this.commitClose) {
                const t = clamp01((this.phaseMs - COMMIT_HOLD_MS) / CLOSE_MS)
                alpha = 1 - easeInQuad(t)
                scale = 1 + 0.05 * easeOutCubic(t)
            } else {
                const t = clamp01(this.phaseMs / CLOSE_MS)
                alpha = 1 - easeInQuad(t)
                scale = 1 - 0.07 * t
            }
        }
        this.alpha = alpha
        this.content.scale.set(scale)

        const flash = this.flashIndex >= 0 ? Math.pow(1 - clamp01(this.flashMs / FLASH_MS), 2) : 0
        const focus = this.slices.reduce((most, slice) => Math.max(most, slice.glow), 0)

        this.light.clear()
        this.slices.forEach((slice, index) => {
            const f = index === this.flashIndex ? flash : 0
            if (slice.glow > 0.003 || f > 0.003) {
                this.drawSectorLight(index, easeOutCubic(slice.glow), f)
            }
        })

        this.slices.forEach((slice, index) => {
            const p = this.revealProgress(index)
            const f = index === this.flashIndex ? flash : 0
            const lit = easeOutCubic(slice.glow)
            const pop = p >= 1 ? 1 : 0.5 + 0.5 * easeOutBack(p)
            const iconScale = this.iconScale * pop * (1 + 0.16 * lit + 0.2 * f)
            // Whatever is NOT under the pointer steps back a little, so the one that is stands out.
            const dim = 1 - 0.3 * focus * (1 - slice.glow)
            slice.icon.scale.set(iconScale)
            slice.iconShadow.scale.set(iconScale)
            slice.icon.alpha = clamp01(p * 1.4) * dim
            slice.iconShadow.alpha = clamp01(p * 1.4) * dim * 0.75
            slice.icon.tint = slice.muted ? lerpColor(MUTED, ICON_INK, lit * 0.5) : lerpColor(ICON_INK, GOLD, Math.max(lit, f))

            // Plaques slide out from the rim as they arrive, and lean out a touch when lit.
            const slide = 9 * (1 - easeOutCubic(p)) + 2 * lit
            slice.plaque.position.set(
                slice.box.x + Math.cos(slice.angle) * slide,
                slice.box.y + Math.sin(slice.angle) * slide,
            )
            // Plates stay solid -- a see-through plate over grass goes green -- and the other names recede
            // by their ink instead.
            slice.plaque.alpha = clamp01(p * 1.3)
            this.drawPlaque(slice, Math.max(lit, f), f)
            const resting = slice.muted ? MUTED : LABEL_INK
            const receded = lerpColor(resting, LABEL_RECEDED, focus * (1 - slice.glow) * (slice.muted ? 0.4 : 1))
            slice.label.tint = lerpColor(receded, 0xffffff, lit)
        })

        const overlay = this.overlay
        overlay.clear()
        if (!this.compact && this.needleAlpha > 0.01) {
            this.drawNeedle(overlay, easeOutCubic(this.needleAlpha))
        }
        if (this.hubGlow > 0.01) {
            const r = this.hubRadius
            overlay.lineStyle({ width: 4, color: GOLD, alpha: 0.18 * this.hubGlow }).drawCircle(0, 0, r + 1.5)
            overlay.lineStyle({ width: 1.4, color: GOLD, alpha: 0.85 * this.hubGlow }).drawCircle(0, 0, r - 0.2)
            overlay.lineStyle(0)
        }
        if (flash > 0.003 && this.flashIndex >= 0 && this.flashIndex < this.slices.length) {
            const [from, to] = this.sliceSpan(this.flashIndex)
            const t = 1 - flash
            const r = this.radius + 2 + 16 * easeOutCubic(t)
            overlay.lineStyle({ width: 1 + 3 * flash, color: GOLD, alpha: 0.8 * flash, cap: LINE_CAP.ROUND })
            overlay.moveTo(Math.cos(from) * r, Math.sin(from) * r)
            overlay.arc(0, 0, r, from, to)
            overlay.lineStyle(0)
        }

        if (this.hubGlyph) {
            this.hubGlyph.scale.set(this.hubGlyph instanceof Graphics ? ((this.hubRadius * 1.02) / WHEEL_ICON_BOX) * (1 + 0.08 * this.hubGlow) : 1)
        }
        if (this.banner) {
            this.drawBanner(this.banner, this.hubGlow)
        }
    }

    // Firelight pooled in one wedge: a warm wash over all of it, brighter toward the rim, a glow under
    // its glyph, and the bezel above it catching the light.
    private drawSectorLight(index: number, lit: number, flash: number) {
        const g = this.light
        const [from, to] = this.sliceSpan(index)
        const inner = this.hubRadius + 2.5
        const outer = this.faceRadius - 0.5
        g.lineStyle(0)
        g.beginFill(EMBER, 0.1 * lit + 0.28 * flash)
        annularSector(g, inner, outer, from, to)
        g.endFill()
        g.beginFill(EMBER, 0.14 * lit + 0.18 * flash)
        annularSector(g, Math.max(inner, outer - 13), outer, from, to)
        g.endFill()
        g.beginFill(GOLD, 0.12 * lit + 0.22 * flash)
        annularSector(g, outer - 5, outer, from, to)
        g.endFill()

        const mid = this.slices[index].angle
        const cx = Math.cos(mid) * this.iconRadius
        const cy = Math.sin(mid) * this.iconRadius
        const size = this.radius * ICON_RATIO
        for (let ring = 0; ring < 5; ring++) {
            g.beginFill(ring < 3 ? EMBER : GOLD, 0.045 * lit).drawCircle(cx, cy, size * (0.85 - ring * 0.12)).endFill()
        }

        const band = this.radius - this.bezelWidth / 2
        g.lineStyle({ width: this.bezelWidth + 3, color: GOLD, alpha: 0.16 * lit + 0.3 * flash, cap: LINE_CAP.ROUND })
        g.moveTo(Math.cos(from) * band, Math.sin(from) * band)
        g.arc(0, 0, band, from, to)
        g.lineStyle({ width: 1.6, color: GOLD, alpha: Math.min(1, 0.85 * lit + 0.4 * flash), cap: LINE_CAP.ROUND })
        g.moveTo(Math.cos(from) * (outer - 0.6), Math.sin(from) * (outer - 0.6))
        g.arc(0, 0, outer - 0.6, from, to)
        g.lineStyle(0)
    }

    // A brass pointer on the hub's rim, aimed at the order under the pointer.
    private drawNeedle(g: Graphics, alpha: number) {
        const a = this.needleAngle
        const r0 = this.hubRadius + 1
        const r1 = this.hubRadius + 7
        const spread = 0.3
        g.beginFill(0x000000, 0.45 * alpha)
            .moveTo(Math.cos(a) * r1, Math.sin(a) * r1 + 1)
            .lineTo(Math.cos(a - spread) * r0, Math.sin(a - spread) * r0 + 1)
            .lineTo(Math.cos(a + spread) * r0, Math.sin(a + spread) * r0 + 1)
            .closePath()
            .endFill()
        g.beginFill(GOLD, alpha)
            .moveTo(Math.cos(a) * r1, Math.sin(a) * r1)
            .lineTo(Math.cos(a - spread) * r0, Math.sin(a - spread) * r0)
            .lineTo(Math.cos(a + spread) * r0, Math.sin(a + spread) * r0)
            .closePath()
            .endFill()
        // The hub's rim warms on the side the needle points to.
        g.lineStyle({ width: 1.6, color: GOLD, alpha: 0.7 * alpha, cap: LINE_CAP.ROUND })
        g.moveTo(Math.cos(a - 0.55) * (this.hubRadius - 0.3), Math.sin(a - 0.55) * (this.hubRadius - 0.3))
        g.arc(0, 0, this.hubRadius - 0.3, a - 0.55, a + 0.55)
        g.lineStyle(0)
    }

    // A cut-timber tag: dropped shadow, dark face, bronze rim that turns gold when lit, one lit edge.
    private drawPlaque(slice: SliceView, lit: number, flash: number) {
        const g = slice.plaqueBg
        const { w, h } = slice.box
        const x = -w / 2
        const y = -h / 2
        g.clear()
        g.beginFill(0x000000, 0.38)
        chamfer(g, x, y + 2, w, h, PLAQUE_CHAMFER)
        g.endFill()
        g.lineStyle({ width: 1, color: lerpColor(RIM, GOLD, lit), alpha: 0.9, join: LINE_JOIN.MITER })
        g.beginFill(lerpColor(PLAQUE_FACE, PLAQUE_FACE_LIT, lit), 0.96)
        chamfer(g, x, y, w, h, PLAQUE_CHAMFER)
        g.endFill()
        g.lineStyle(0)
        if (flash > 0.003) {
            g.beginFill(GOLD, 0.28 * flash)
            chamfer(g, x, y, w, h, PLAQUE_CHAMFER)
            g.endFill()
        }
        g.lineStyle(1, RIM_LIGHT, 0.12 + 0.2 * lit)
            .moveTo(x + PLAQUE_CHAMFER, y + 1.5)
            .lineTo(x + w - PLAQUE_CHAMFER, y + 1.5)
        g.lineStyle(0)
    }

    // The global wheel's title plate, with a switch chevron at each end.
    private drawBanner(banner: BannerView, lit: number) {
        const g = banner.bg
        const { w, h } = banner.box
        const x = -w / 2
        const y = -h / 2
        g.clear()
        g.beginFill(0x000000, 0.38)
        chamfer(g, x, y + 2, w, h, PLAQUE_CHAMFER + 1)
        g.endFill()
        g.lineStyle({ width: 1, color: lerpColor(RIM, GOLD, lit), alpha: 0.95, join: LINE_JOIN.MITER })
        g.beginFill(lerpColor(PLAQUE_FACE, PLAQUE_FACE_LIT, lit * 0.6), 0.97)
        chamfer(g, x, y, w, h, PLAQUE_CHAMFER + 1)
        g.endFill()
        g.lineStyle(1, RIM_LIGHT, 0.14 + 0.2 * lit).moveTo(x + PLAQUE_CHAMFER + 1, y + 1.5).lineTo(x + w - PLAQUE_CHAMFER - 1, y + 1.5)
        g.lineStyle(0)
        const chevron = lerpColor(RIM_LIGHT, GOLD, lit)
        const cx = w / 2 - 9
        g.beginFill(chevron, 0.75 + 0.25 * lit)
        g.moveTo(-cx - 3, 0).lineTo(-cx + 2, -4).lineTo(-cx + 2, 4).closePath()
        g.moveTo(cx + 3, 0).lineTo(cx - 2, -4).lineTo(cx - 2, 4).closePath()
        g.endFill()
    }

    // ---- per-frame ----

    // Advance the wheel's motion by one frame. A resting wheel returns at once.
    public animate(deltaMs: number) {
        if (!this.visible) {
            return
        }
        // Not capped: after a hitch the motion catches up rather than playing on late.
        const dt = Math.max(0, deltaMs || 0)
        let dirty = false

        if (this.phase === "opening") {
            this.phaseMs += dt
            if (this.phaseMs >= OPEN_MS) this.phase = "open"
            dirty = true
        } else if (this.phase === "closing") {
            this.phaseMs += dt
            if (this.phaseMs >= CLOSE_MS + (this.commitClose ? COMMIT_HOLD_MS : 0)) {
                this.finishClose()
                return
            }
            dirty = true
        }

        if (this.revealMs !== undefined) {
            this.revealMs += dt
            if (this.revealMs >= this.revealTotalMs) this.revealMs = undefined
            dirty = true
        }

        const step = dt / HOVER_MS
        this.slices.forEach((slice, index) => {
            const target = index === this.hoveredCommandIndex ? 1 : 0
            if (slice.glow !== target) {
                slice.glow = approach(slice.glow, target, step)
                dirty = true
            }
        })

        const hubTarget = this.hubHovered && this.switchable ? 1 : 0
        if (this.hubGlow !== hubTarget) {
            this.hubGlow = approach(this.hubGlow, hubTarget, dt / HUB_HOVER_MS)
            dirty = true
        }

        const hovered = this.slices[this.hoveredCommandIndex]
        const needleTarget = hovered ? 1 : 0
        if (hovered) {
            const target = hovered.angle
            if (this.needleAlpha < 0.02) {
                this.needleAngle = target
            } else {
                const delta = wrapAngle(target - this.needleAngle)
                if (Math.abs(delta) > 0.001) {
                    this.needleAngle += delta * (1 - Math.exp(-dt / NEEDLE_MS))
                    dirty = true
                }
            }
        }
        if (this.needleAlpha !== needleTarget) {
            this.needleAlpha = approach(this.needleAlpha, needleTarget, dt / HOVER_MS)
            dirty = true
        }

        if (this.flashIndex >= 0) {
            this.flashMs += dt
            if (this.flashMs >= FLASH_MS) this.flashIndex = -1
            dirty = true
        }

        if (dirty) {
            this.paint()
        }
    }

    // ---- input ----

    // What a point (in the wheel's own coordinates) lands on. Slices are picked by direction, out to a
    // little past the rim, and their plaques are targets in their own right.
    private targetAt(x: number, y: number): PointerTarget {
        const distance = Math.hypot(x, y)
        if (this.compact) {
            return distance <= this.radius + 3 || this.slices.some(slice => this.inBox(x, y, slice.box, PLAQUE_HIT_PAD))
                ? { kind: "slice", index: 0 } : null
        }
        if (distance <= this.hubRadius + 2) {
            return { kind: "hub" }
        }
        if (this.banner && this.inBox(x, y, this.banner.box, PLAQUE_HIT_PAD)) {
            return { kind: "banner", step: x < 0 ? -1 : 1 }
        }
        const count = this.commandsOf(this.npcTypes[this.currentNPCIndex]).length
        if (this.slices.length === count) {
            const onPlaque = this.slices.findIndex(slice => this.inBox(x, y, slice.box, PLAQUE_HIT_PAD))
            if (onPlaque >= 0) {
                return { kind: "slice", index: onPlaque }
            }
        }
        if (distance <= this.radius + RING_SLOP && count > 0) {
            return { kind: "slice", index: sliceIndexAt(x, y, count) }
        }
        return null
    }

    private inBox(x: number, y: number, box: PlaqueBox, pad: number) {
        return Math.abs(x - box.x) <= box.w / 2 + pad && Math.abs(y - box.y) <= box.h / 2 + pad
    }

    // The hit area: the whole disc (a press in the hub of a locked wheel is still ON the wheel, not a
    // press on the world behind it) plus everything targetAt answers to.
    private coversPoint(x: number, y: number) {
        return Math.hypot(x, y) <= this.radius || this.targetAt(x, y) !== null
    }

    private setHover(index: number, hub: boolean) {
        this.hubHovered = hub
        this.cursor = index >= 0 || (hub && this.switchable) ? "pointer" : "default"
        if (index === this.hoveredCommandIndex) {
            return
        }
        this.hoveredCommandIndex = index
        if (index >= 0 && this.phase !== "closing") {
            this.onHoverChange?.()
        }
    }

    private onPointerMove(event: any) {
        // Stop event propagation to prevent interfering with game mouse handling
        event.stopPropagation()
        const local = this.toLocal(event.data.global)
        this.hoverAt(local.x, local.y)
    }

    private onPointerDown(event: any) {
        // Stop event propagation to prevent triggering game attacks
        event.stopPropagation()
        const local = this.toLocal(event.data.global)
        this.pressAt(local.x, local.y)
    }

    // A pointer resting at (x, y) in the wheel's own coordinates. Public, with pressAt, so a
    // demonstration can work the real wheel by hand (gui/forager_tutorial.ts) exactly as a mouse would.
    public hoverAt(x: number, y: number) {
        const target = this.targetAt(x, y)
        this.setHover(target?.kind === "slice" ? target.index : -1, target?.kind === "hub" || target?.kind === "banner")
    }

    // A press at (x, y) in the wheel's own coordinates.
    public pressAt(x: number, y: number) {
        const target = this.targetAt(x, y)
        if (!target) {
            return
        }
        if (target.kind === "hub") {
            this.switchNPC(1)
            return
        }
        if (target.kind === "banner") {
            this.switchNPC(target.step)
            return
        }

        const currentNPC = this.npcTypes[this.currentNPCIndex]
        const command = this.commandsOf(currentNPC)[target.index]
        if (!command) {
            return
        }
        this.activeCommandIndex = target.index
        // Lit BEFORE the callback: a per-entity wheel is put away from inside it, and the exit has to
        // know it is carrying a pick (it holds the flash, then lifts away) rather than a dismissal.
        this.flashIndex = target.index
        this.flashMs = 0
        this.hoveredCommandIndex = target.index
        this.paint()
        this.onCommandSelect?.(command.name, currentNPC.name)
    }

    private onPointerOut() {
        this.setHover(-1, false)
    }

    // Turn the global wheel to the next (step 1) or previous (step -1) role.
    private switchNPC(step: number = 1) {
        // When bound to a single NPC the page is locked to that NPC's role.
        if (!this.switchable) {
            return
        }
        const count = this.npcTypes.length
        this.currentNPCIndex = (this.currentNPCIndex + step + count) % count
        this.rolePageIndex = this.currentNPCIndex
        this.activeCommandIndex = -1
        this.hoveredCommandIndex = -1
        this.flashIndex = -1
        this.layout()
        // The new page's orders fan out the way the first ones did.
        this.revealMs = 0
        this.onHoverChange?.()
        this.paint()
    }

    // A page change. Deferred while the wheel is on its way out (see layoutPending).
    private requestLayout() {
        if (this.phase === "closing") {
            this.layoutPending = true
            return
        }
        this.layout()
    }

    public getCurrentNPC(): string {
        return this.npcTypes[this.currentNPCIndex].name
    }

    public getActiveCommand(): string | null {
        if (this.activeCommandIndex >= 0) {
            return this.commandsOf(this.npcTypes[this.currentNPCIndex])[this.activeCommandIndex]?.name ?? null
        }
        return null
    }

    // Forget the pick and the hover. The pick's FLASH is left to finish -- it is what the player
    // sees of their choice as the wheel goes.
    public clearSelection() {
        this.activeCommandIndex = -1
        this.hubHovered = false
        // A wheel on its way out keeps the picked wedge lit until it has gone.
        if (this.phase !== "closing") {
            this.hoveredCommandIndex = -1
        }
    }

    // Bind the wheel to a single NPC and lock it to that NPC's role page. Used when the wheel is
    // opened from an entity's command button (server tells us which role).
    public bindToEntity(targetEntityId: string, role: CommandWheelRole, commands?: NPCCommand[]) {
        this.compactRequested = false
        if (role === "lord" && !this.npcTypes.some(npc => npc.name === "lord")) {
            this.npcTypes.push({ name: "lord", displayName: "Lord", commands: [
                { name: "lord:follow", displayName: "Follow Me" },
                { name: "lord:stay", displayName: "Stay Here" },
                { name: "lord:seek_fire", displayName: "Seek fire" },
                { name: "lord:watch", displayName: "Watch" },
            ] })
        }
        this.commandOverride = commands
        this.targetEntityId = targetEntityId
        this.roleLocked = true
        const roleIndex = this.npcTypes.findIndex(npc => npc.name === role)
        this.currentNPCIndex = roleIndex >= 0 ? roleIndex : this.currentNPCIndex
        if (role !== "lord") this.rolePageIndex = this.currentNPCIndex
        this.resetPointerState()
        this.layout()
        this.replayRevealIfOpen()
    }

    // Bind the wheel to one entity's interactions: a synthetic single page whose "commands" are the
    // interaction types, locked (there is nothing to switch to). Reusing the role wheel's page shape
    // means the geometry, slice drawing, hover/selection states and motion all come for free; the
    // only thing gui_manager has to know is that a selection on the INTERACTION_PAGE page is an
    // interaction rather than a squad command.
    public bindToInteractions(targetEntityId: string, commands: NPCCommand[], emoji: string = "✋", compact = false) {
        this.compactRequested = compact
        this.commandOverride = undefined
        this.targetEntityId = targetEntityId
        this.roleLocked = true
        this.npcTypes = [
            ...this.npcTypes.filter(npc => npc.name !== INTERACTION_PAGE),
            {
                name: INTERACTION_PAGE,
                displayName: "Interactions",
                emoji,
                commands: compact ? commands.slice(0, 1) : commands,
            },
        ]
        this.currentNPCIndex = this.npcTypes.length - 1
        this.resetPointerState()
        this.layout()
        this.replayRevealIfOpen()
    }

    // A small medallion and one readable plaque. The entire disc, including its centre, picks
    // the sole command; the normal wheel keeps its role-switching hub and surrounding slices.
    public bindToSingleInteraction(targetEntityId: string, command: NPCCommand) {
        this.bindToInteractions(targetEntityId, [command], "✋", true)
    }

    // Return to the global (broadcast) wheel: no bound entity, role switching re-enabled.
    public clearTarget() {
        this.compactRequested = false
        const wasLocked = this.roleLocked
        this.commandOverride = undefined
        this.targetEntityId = undefined
        this.roleLocked = false
        // Drop any synthetic interaction page so the global (Control-key) wheel can't cycle onto one
        // entity's verbs, and so the switch counts roles again.
        const hadInteractions = this.npcTypes.some(npc => (npc.name === INTERACTION_PAGE || npc.name === "lord"))
        if (hadInteractions) {
            this.npcTypes = this.npcTypes.filter(npc => npc.name !== INTERACTION_PAGE && npc.name !== "lord")
            this.currentNPCIndex = Math.min(this.rolePageIndex, this.npcTypes.length - 1)
        }
        if (wasLocked || hadInteractions) {
            this.requestLayout()
        }
    }

    // Re-bound while already up (another member tapped, or the player walked into reach of a verb):
    // the orders are new, so they arrive the way the first ones did.
    private replayRevealIfOpen() {
        if (this.opened && this.phase !== "closing") {
            this.revealMs = 0
            this.paint()
        }
    }

    private resetPointerState() {
        this.activeCommandIndex = -1
        this.hoveredCommandIndex = -1
        this.hubHovered = false
        this.flashIndex = -1
        this.needleAlpha = 0
        this.hubGlow = 0
    }

    public getTargetEntityId(): string | undefined {
        return this.targetEntityId
    }

    // Where a pointer goes to pick `command` on the page showing -- its glyph, in the wheel's own
    // coordinates -- or undefined when the page does not offer it. For a demonstration working the
    // wheel (gui/forager_tutorial.ts), which aims by the real layout rather than a copy of it.
    public aimAt(command: string): { x: number; y: number } | undefined {
        const slice = this.slices.find(next => next.command.name === command)
        return slice && { x: Math.cos(slice.angle) * this.iconRadius, y: Math.sin(slice.angle) * this.iconRadius }
    }

    public setVisible(visible: boolean) {
        if (visible) {
            this.fadeIn()
        } else {
            this.fadeOut()
        }
    }

    // Whether the wheel is logically open — true from the moment it starts opening until it starts
    // closing. Use this (not `.visible`) for input gating so releasing the wheel frees clicks
    // immediately rather than after the exit has played.
    public isOpen(): boolean {
        return this.opened
    }

    // Bring the wheel up: it pops open and its orders fan out. Interrupting a close picks up from the
    // current fade rather than snapping.
    public fadeIn() {
        if (this.opened && this.phase !== "closing") {
            return
        }
        this.opened = true
        this.interactive = true
        if (this.layoutPending) {
            this.layout()
        }
        const reopening = this.phase === "closing" && this.visible
        this.phase = "opening"
        this.phaseMs = reopening ? OPEN_MS * clamp01(this.alpha) : 0
        this.commitClose = false
        this.flashIndex = -1
        // The orders fan out on every open, a re-open mid-exit included: whatever is coming up may
        // be a different member's.
        this.revealMs = 0
        if (!reopening) {
            this.needleAlpha = 0
            this.slices.forEach(slice => (slice.glow = 0))
        }
        this.visible = true
        this.paint()
    }

    // Put the wheel away. A wheel carrying a fresh pick holds its flash a beat and lifts away; any
    // other close sinks back. Either way it stops taking presses at once.
    public fadeOut() {
        this.opened = false
        if (!this.visible || this.phase === "closing") {
            return
        }
        this.interactive = false
        this.commitClose = this.flashIndex >= 0
        this.phase = "closing"
        this.phaseMs = 0
        this.setHover(this.commitClose ? this.flashIndex : -1, false)
    }

    private finishClose() {
        this.phase = "closed"
        this.visible = false
        this.alpha = 1
        this.interactive = true
        this.flashIndex = -1
        this.revealMs = undefined
        this.resetPointerState()
        this.slices.forEach(slice => (slice.glow = 0))
        if (this.layoutPending) {
            this.layout()
        } else {
            this.paint()
        }
    }
}
